import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Plus, PenLine, Play, RotateCcw, X, ArrowUp, ArrowDown, Trash2, Check,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import {
  listProductionScenarios, createProductionScenario, updateProductionScenario,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

function ProductionTabs() {
  const link = (to, label, testId) => (
    <NavLink to={to} end
             className="h-9 px-3.5 rounded-full text-[12.5px] flex items-center transition"
             style={({ isActive }) => ({
               background: isActive ? "#4A3728" : "#F7F1E7",
               color: isActive ? "#F7EFE0" : "#4A3728",
               boxShadow: isActive ? "none" : "inset 0 0 0 1px #EFE4D0",
             })}
             data-testid={testId}>
      {label}
    </NavLink>
  );
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="mo-production-tabs">
      {link("/master-order/production", "План", "mo-tab-plan")}
      {link("/master-order/production/scenarios",
            "Мои производственные сценарии", "mo-tab-scenarios")}
    </div>
  );
}

function StageEditor({ stages, onChange, canWrite }) {
  const add = () => onChange([...stages, { title: "", default_duration_minutes: null, notes: "", sort_order: stages.length }]);
  const update = (i, patch) => onChange(stages.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(stages.filter((_, k) => k !== i).map((s, k) => ({ ...s, sort_order: k })));
  const move = (i, dir) => {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= stages.length) return;
    const next = [...stages];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((s, k) => ({ ...s, sort_order: k })));
  };
  return (
    <div className="space-y-2" data-testid="mo-scn-editor-stages">
      {stages.map((s, i) => (
        <div key={i} className="rounded-lg bg-white px-3 py-2.5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-scn-editor-stage">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-[10.5px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>
              Этап {i + 1}
            </div>
            {canWrite && (
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => move(i, "up")} disabled={i === 0} data-testid="mo-scn-stage-up"
                        className="w-7 h-7 rounded-full grid place-items-center disabled:opacity-30"
                        style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }} aria-label="Выше">
                  <ArrowUp size={12} />
                </button>
                <button onClick={() => move(i, "down")} disabled={i === stages.length - 1} data-testid="mo-scn-stage-down"
                        className="w-7 h-7 rounded-full grid place-items-center disabled:opacity-30"
                        style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }} aria-label="Ниже">
                  <ArrowDown size={12} />
                </button>
                <button onClick={() => remove(i)} data-testid="mo-scn-stage-delete"
                        className="w-7 h-7 rounded-full grid place-items-center"
                        style={{ background: "#F7F1E7", color: "#8B4B2E", boxShadow: "inset 0 0 0 1px #EFE4D0" }} aria-label="Удалить">
                  <Trash2 size={12} />
                </button>
              </div>
            )}
          </div>
          <input value={s.title} disabled={!canWrite}
                 onChange={(e) => update(i, { title: e.target.value })}
                 placeholder="Название этапа"
                 className="w-full h-9 px-3 rounded-md bg-white text-[13px] outline-none disabled:opacity-70"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-scn-stage-title" />
          <div className="grid grid-cols-1 gap-1.5 mt-1.5">
            <input value={s.notes || ""} disabled={!canWrite}
                   onChange={(e) => update(i, { notes: e.target.value })}
                   placeholder="Заметка"
                   className="h-9 px-2 rounded-md bg-white text-[12.5px] outline-none disabled:opacity-70"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-scn-stage-notes" />
          </div>
        </div>
      ))}
      {canWrite && (
        <button onClick={add}
                className="h-9 w-full rounded-lg text-[12.5px] inline-flex items-center justify-center gap-1.5"
                style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                data-testid="mo-scn-stage-add">
          <Plus size={13} /> Добавить этап
        </button>
      )}
    </div>
  );
}

function ScenarioEditor({ initial, onClose, onSaved, canWrite }) {
  const isNew = !initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [stages, setStages] = useState(initial?.stages ? initial.stages.map((s, i) => ({ ...s, sort_order: i })) : []);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Название сценария не может быть пустым"); return; }
    for (const s of stages) {
      if (!(s.title || "").trim()) { toast.error("Все этапы должны иметь название"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        notes: notes.trim() || null,
        stages: stages.map((s, i) => ({
          ...(s.id ? { id: s.id } : {}),
          title: s.title.trim(),
          default_duration_minutes: s.default_duration_minutes ? Number(s.default_duration_minutes) : null,
          notes: (s.notes || "").trim() || null,
          sort_order: i,
        })),
      };
      const saved = isNew
        ? await createProductionScenario(payload)
        : await updateProductionScenario(initial.id, payload);
      toast.success(isNew ? "Сценарий сохранён" : "Изменения сохранены");
      onSaved?.(saved);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
         data-testid="mo-scn-editor">
      <div className="w-full md:max-w-xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl p-5 md:p-6 max-h-[92vh] overflow-y-auto"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-[19px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
            {isNew ? "Новый сценарий" : "Изменить сценарий"}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full grid place-items-center"
                  style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                  data-testid="mo-scn-editor-close" aria-label="Закрыть">
            <X size={13} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Название</div>
            <input value={name} disabled={!canWrite} onChange={(e) => setName(e.target.value)}
                   placeholder="Например: Торт"
                   className="w-full h-10 px-3 rounded-lg bg-white text-[13px] outline-none disabled:opacity-70"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-scn-editor-name" />
          </div>
          <div>
            <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Заметка</div>
            <textarea value={notes} disabled={!canWrite} onChange={(e) => setNotes(e.target.value)} rows={2}
                      placeholder="Необязательно"
                      className="w-full px-3 py-2 rounded-lg bg-white text-[13px] outline-none resize-none disabled:opacity-70"
                      style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                      data-testid="mo-scn-editor-notes" />
          </div>
          <div>
            <div className="text-[10.5px] tracking-[0.14em] uppercase mb-1" style={{ color: "#8B6F4B" }}>Этапы</div>
            <StageEditor stages={stages} onChange={setStages} canWrite={canWrite} />
          </div>
        </div>
        {canWrite && (
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-full text-[13px]"
                    style={{ background: "transparent", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-scn-editor-cancel">
              Отмена
            </button>
            <button onClick={save} disabled={saving} className="h-10 px-5 rounded-full text-[13px]"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-scn-editor-save">
              {saving ? "Сохраняем..." : "Сохранить"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ScenarioCard({ scenario, canWrite, onEdit, onToggleActive }) {
  const active = scenario.is_active;
  return (
    <div className="rounded-2xl bg-white px-4 py-3.5 md:px-5 md:py-4"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", opacity: active ? 1 : 0.72 }}
         data-testid="mo-scn-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] md:text-[16px]"
               style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
               data-testid="mo-scn-card-name">
            {scenario.name}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px]"
               style={{ color: "#8B6F4B" }}>
            <span data-testid="mo-scn-card-stages">
              {scenario.stages.length} {scenario.stages.length === 1 ? "этап"
                                    : scenario.stages.length < 5 ? "этапа" : "этапов"}
            </span>
            <span>·</span>
            <span data-testid="mo-scn-card-state">{active ? "Используется" : "Не используется"}</span>
          </div>
          {scenario.notes && (
            <div className="mt-1.5 text-[12px]" style={{ color: "#5B4326" }}>{scenario.notes}</div>
          )}
        </div>
        {canWrite && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onToggleActive(scenario)}
                    className="h-9 px-3 rounded-full text-[12px] inline-flex items-center gap-1.5"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-scn-card-toggle">
              {active ? <><X size={12} /> Убрать из используемых</> : <><RotateCcw size={12} /> Вернуть в используемые</>}
            </button>
            <button onClick={() => onEdit(scenario)}
                    className="w-9 h-9 rounded-full grid place-items-center"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-scn-card-edit" aria-label="Редактировать">
              <PenLine size={13} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MOProductionScenarios() {
  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | scenario object for edit

  const load = useCallback(async () => {
    setLoading(true);
    try { setScenarios(await listProductionScenarios()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (scn) => {
    try {
      await updateProductionScenario(scn.id, { is_active: !scn.is_active });
      toast.success(scn.is_active ? "Убрано из используемых" : "Возвращено в используемые");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const body = (() => {
    if (aLoad) return <LoadingBlock label="Проверяем доступ..." />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;
    return (
      <>
        <ProductionTabs />
        <div className="text-[12.5px] mb-3" style={{ color: "#5B4326" }} data-testid="mo-scn-lede">
          Сохраните последовательность этапов, которую Вы часто используете, и добавляйте её в новые заказы.
        </div>
        {canWrite && (
          <div className="mb-3 flex justify-end">
            <button onClick={() => setEditing({})}
                    className="h-10 px-4 rounded-full text-[13px] inline-flex items-center gap-2"
                    style={{ background: "#4A3728", color: "#F7EFE0" }}
                    data-testid="mo-scn-add">
              <Plus size={14} /> Новый сценарий
            </button>
          </div>
        )}
        {loading ? <LoadingBlock label="Загружаем сценарии..." />
        : scenarios.length === 0 ? (
          <div className="rounded-2xl bg-white p-8 md:p-10 text-center"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-scn-empty">
            <div className="text-[16px] md:text-[18px]"
                 style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
              У Вас пока нет производственных сценариев
            </div>
            <div className="text-[12.5px] mt-1.5 mx-auto max-w-md" style={{ color: "#8B6F4B" }}>
              Сохраните удобную последовательность этапов — и добавляйте её в новые заказы одной кнопкой.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="mo-scn-list">
            {scenarios.map(s => (
              <ScenarioCard key={s.id} scenario={s} canWrite={canWrite}
                            onEdit={setEditing}
                            onToggleActive={toggleActive} />
            ))}
          </div>
        )}
        {editing !== null && (
          <ScenarioEditor initial={editing} canWrite={canWrite}
                          onClose={() => setEditing(null)}
                          onSaved={load} />
        )}
      </>
    );
  })();

  return (
    <MOShell active="production" title="Мои производственные сценарии"
             subtitle="Быстро добавляйте один и тот же набор этапов в новые заказы">
      {body}
    </MOShell>
  );
}
